/**
 * «Dov'è la merce»: la quantità ordinata divisa nei cinque posti in cui può
 * stare — già ritirata, pronta a magazzino, in produzione, ancora da produrre,
 * oppure **non valutata**.
 *
 * Serve a rispondere senza aprire niente alla domanda che il cruscotto finora
 * non affrontava: una commessa è ferma perché la merce non c'è, o perché il
 * cliente non viene a prenderla? (ragionamento con Enrico del 16-08-2026).
 *
 * Due misure dello stesso oggetto: `compatta` sta in una cella di tabella,
 * quella grande è la fotografia del portafoglio in testata.
 */
import type { DoveLaMerce } from "@/types/api";
import { cn } from "@/lib/utils";

/**
 * I segmenti, nell'ordine del ciclo di vita della merce.
 *
 * Il quinto è arrivato il 18-08 e ha una ragione precisa: le righe il cui articolo
 * è marcato «materiale di consumo» non vengono valutate dal motore, e finché
 * finivano in «da produrre» la barra diceva la cosa più allarmante possibile
 * (rosso) proprio per i pallet che avevamo deciso di non tracciare. Il tratteggio
 * grigio dice «fuori dal conto per scelta», che è la verità.
 */
const SEGMENTI = [
  { key: "ritirato", label: "già ritirato", classe: "bg-slate-400 dark:bg-slate-600" },
  { key: "magazzino", label: "pronto a magazzino", classe: "bg-emerald-600 dark:bg-emerald-500" },
  { key: "produzione", label: "in produzione", classe: "bg-amber-500 dark:bg-amber-400" },
  { key: "da_produrre", label: "da produrre", classe: "bg-rose-700/80 dark:bg-rose-500/80" },
  {
    key: "non_valutato",
    label: "materiale di consumo, non valutato",
    classe:
      "bg-muted [background-image:repeating-linear-gradient(45deg,transparent_0_3px,var(--border)_3px_6px)]",
  },
] as const;

function n(v: string | number | null | undefined): number {
  return Number(v ?? 0);
}

function pezzi(m: DoveLaMerce | undefined) {
  if (!m) return { valori: [] as { key: string; label: string; classe: string; valore: number }[], totale: 0 };
  const valori = SEGMENTI.map((s) => ({ ...s, valore: n(m[s.key as keyof DoveLaMerce]) }));
  return { valori, totale: valori.reduce((acc, v) => acc + v.valore, 0) };
}

const fmt = (v: number) =>
  v.toLocaleString("it-IT", { maximumFractionDigits: 0, useGrouping: true });

/**
 * Versione da tabella: la barra più la parola che conta. Sotto la barra non
 * ripetiamo tutti e quattro i numeri — si legge la cosa più utile per la riga,
 * cioè quanta merce sta ferma pronta (o, se non ce n'è, quanta manca).
 */
export function DoveLaMerceCella({ merce }: { merce?: DoveLaMerce }) {
  const { valori, totale } = pezzi(merce);
  if (!totale) return <span className="text-muted-foreground/50">—</span>;

  const q = (k: string) => valori.find((v) => v.key === k)?.valore ?? 0;
  const magazzino = q("magazzino");
  const produzione = q("produzione");
  const daProdurre = q("da_produrre");
  const nonValutato = q("non_valutato");
  const nota =
    magazzino > 0
      ? `${fmt(magazzino)} pronti`
      : daProdurre > 0
        ? `${fmt(daProdurre)} da produrre`
        : produzione > 0
          ? `${fmt(produzione)} in produzione`
          : nonValutato > 0
            ? // Le due commesse fatte solo di pallet EPAL finivano qui e la riga
              // diceva «tutto ritirato» pur avendo merce: la nota va detta.
              `${fmt(nonValutato)} non valutati`
            : "tutto ritirato";

  const titolo = valori
    .filter((v) => v.valore > 0)
    .map((v) => `${v.label}: ${fmt(v.valore)}`)
    .join(" · ");

  return (
    <div className="flex flex-col gap-1" title={titolo}>
      <div className="flex h-2 w-full overflow-hidden rounded-sm bg-muted">
        {valori
          .filter((v) => v.valore > 0)
          .map((v) => (
            <span
              key={v.key}
              className={cn("block h-full", v.classe)}
              style={{ width: `${(100 * v.valore) / totale}%` }}
            />
          ))}
      </div>
      <span className="text-[10px] leading-none text-muted-foreground">{nota}</span>
    </div>
  );
}

/**
 * Versione grande: la stessa scomposizione su tutto il portafoglio, con la
 * legenda. È il numero che serve alla direzione — quanta merce nostra sta ferma
 * perché il cliente non la ritira.
 */
export function DoveLaMerceFotografia({
  merce,
  titolo = "Dov'\u00e8 la merce delle commesse aperte",
}: {
  merce?: DoveLaMerce;
  /** Il titolo si passa perché la stessa barra descrive **due oggetti diversi**:
   *  tutto il portafoglio nel Cruscotto, una sola commessa nel suo drawer. Con
   *  l'intestazione cablata, il drawer della #596 annunciava «le commesse
   *  aperte — 28.000 pezzi», cioè un numero giusto con il nome sbagliato. */
  titolo?: string;
}) {
  const { valori, totale } = pezzi(merce);

  return (
    <section className="h-fit rounded-lg border bg-card p-3">
      <h2 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {titolo}
        {totale > 0 && <span className="normal-case tracking-normal"> — {fmt(totale)} pezzi</span>}
      </h2>
      {totale === 0 ? (
        <p className="mt-2 text-xs text-muted-foreground">Nessun dato disponibile.</p>
      ) : (
        <>
          <div className="mt-2 flex h-3.5 w-full overflow-hidden rounded-sm bg-muted">
            {valori
              .filter((v) => v.valore > 0)
              .map((v) => (
                <span
                  key={v.key}
                  className={cn("block h-full", v.classe)}
                  style={{ width: `${(100 * v.valore) / totale}%` }}
                />
              ))}
          </div>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {valori.map((v) => (
              <li key={v.key} className="inline-flex items-center gap-1.5 text-[11px]">
                <span className={cn("size-2 rounded-[2px]", v.classe)} aria-hidden="true" />
                <span className="text-muted-foreground">{v.label}</span>
                <span className="font-medium tabular-nums">{fmt(v.valore)}</span>
                <span className="text-muted-foreground/70">
                  {Math.round((100 * v.valore) / totale)}%
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
